import { useEffect, useState, useRef, useCallback } from 'react';
import { useConversation, useMessages } from '@/hooks/useMessages';
import { useMessagingSocket } from '@/hooks/useMessagingSocket';
import { useMessagingStore } from '@/store/messaging.store';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { TypingIndicator } from './TypingIndicator';
import { ConversationSettings } from './ConversationSettings';
import { ForwardMessageModal } from './ForwardMessageModal';
import { MessageSearch } from './MessageSearch';
import type { MessageWithDetails } from '@/types/messaging';

import { IconButton } from '../common/Button';
import FaIcon from '../common/FaIcon';
import { toast } from '../common/Toast';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

interface ConversationViewProps {
  conversationId: string;
}

export function ConversationView({ conversationId }: ConversationViewProps) {
  const [showSettings, setShowSettings] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [forwardingMessage, setForwardingMessage] = useState<MessageWithDetails | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const dragCounterRef = useRef(0);
  const { data: conversation, isLoading: convLoading } = useConversation(conversationId);
  const { data: messagesData, isLoading: messagesLoading } = useMessages(conversationId);

  const { joinConversation, leaveConversation, markConversationAsRead } = useMessagingSocket();
  const { setMessages, clearMessages, clearUnreadCount, typingUsers, setActiveConversation } = useMessagingStore();

  // Join conversation room and mark as read
  useEffect(() => {
    if (conversationId) {
      setActiveConversation(conversationId);
      joinConversation(conversationId);
      markConversationAsRead(conversationId);
      clearUnreadCount(conversationId);
    }

    return () => {
      if (conversationId) {
        leaveConversation(conversationId);
        clearMessages(conversationId);
        setActiveConversation(null);
      }
    };
  }, [conversationId, joinConversation, leaveConversation, markConversationAsRead, clearUnreadCount, clearMessages, setActiveConversation]);

  // Update store when messages load
  useEffect(() => {
    if (messagesData?.data && conversationId) {
      setMessages(conversationId, messagesData.data);
    }
  }, [messagesData, conversationId, setMessages]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const validFiles: File[] = [];
    let rejectedCount = 0;

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        rejectedCount++;
      } else {
        validFiles.push(file);
      }
    }

    if (rejectedCount > 0) {
      toast.warning(
        'Files Too Large',
        `${rejectedCount} file${rejectedCount > 1 ? 's' : ''} exceeded the 10MB limit`
      );
    }

    if (validFiles.length > 0) {
      setDroppedFiles(validFiles);
    }
  }, []);

  const handleDroppedFilesProcessed = useCallback(() => {
    setDroppedFiles([]);
  }, []);

  if (convLoading || messagesLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <svg className="animate-spin h-8 w-8 text-primary-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-theme-tertiary">
        <p>Conversation not found</p>
      </div>
    );
  }

  const currentTypingUsers = typingUsers[conversationId] || [];

  return (
    <div
      className="flex flex-col h-full relative"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag-and-drop overlay */}
      {isDragOver && (
        <div className="absolute inset-0 z-50 pointer-events-none">
          <div className="absolute inset-2 border-2 border-dashed border-primary-500 rounded-lg bg-primary-500/10 flex flex-col items-center justify-center">
            <FaIcon icon="cloud-arrow-up" className="w-12 h-12 text-primary-500 mb-3" />
            <p className="text-lg font-medium text-primary-600 dark:text-primary-400">Drop files to upload</p>
            <p className="text-sm text-theme-tertiary mt-1">Files will be sent as messages</p>
          </div>
        </div>
      )}
      {/* Conversation Header */}
      <div className="px-4 py-2 border-b border-theme-primary bg-surface-primary">
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div className="flex-shrink-0">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm ${
                conversation.type === 'direct'
                  ? 'bg-primary-500'
                  : conversation.type === 'group'
                  ? 'bg-purple-500'
                  : 'bg-orange-500'
              }`}
            >
              {conversation.type === 'direct' && conversation.other_participant
                ? conversation.other_participant.name.charAt(0).toUpperCase()
                : conversation.type === 'incident'
                ? '!'
                : '#'}
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-medium text-theme-primary truncate">
              {conversation.type === 'direct' && conversation.other_participant
                ? conversation.other_participant.name
                : conversation.name || 'Unnamed'}
            </h3>
            {conversation.type !== 'direct' && conversation.participant_count && (
              <p className="text-xs text-theme-tertiary">
                {conversation.participant_count} members
              </p>
            )}
          </div>
          {/* Search button */}
          <IconButton
            icon={<FaIcon icon="magnifying-glass" className="w-5 h-5" />}
            label="Search messages"
            variant="ghost"
            size="sm"
            onClick={() => setShowSearch(true)}
          />
          {/* Settings button */}
          <IconButton
            icon={<FaIcon icon="ellipsis-vertical" className="w-5 h-5" />}
            label="Conversation settings"
            variant="ghost"
            size="sm"
            onClick={() => setShowSettings(true)}
          />
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <ConversationSettings
          conversation={conversation}
          onClose={() => setShowSettings(false)}
          onLeaveGroup={() => {
            setShowSettings(false);
            setActiveConversation(null);
          }}
        />
      )}

      {/* Search Overlay */}
      {showSearch && (
        <MessageSearch
          conversationId={conversationId}
          onClose={() => setShowSearch(false)}
        />
      )}

      {/* Forward Modal */}
      {forwardingMessage && (
        <ForwardMessageModal
          message={forwardingMessage}
          currentConversationId={conversationId}
          onClose={() => setForwardingMessage(null)}
        />
      )}

      {/* Messages */}
      <div className="flex-1 overflow-hidden">
        <MessageList
          conversationId={conversationId}
          onForward={setForwardingMessage}
        />
      </div>

      {/* Typing Indicator */}
      {currentTypingUsers.length > 0 && (
        <TypingIndicator users={currentTypingUsers} />
      )}

      {/* Input */}
      <MessageInput
        conversationId={conversationId}
        droppedFiles={droppedFiles}
        onDroppedFilesProcessed={handleDroppedFilesProcessed}
      />
    </div>
  );
}
